import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { createSelector } from 'reselect';
import get from 'lodash/get';
import flatten from 'lodash/flatten';
import moment from 'moment-timezone';
import axiosInstance from "../../config/axiosInstance";
import ENDPOINTS from "../../config/apiUrls";
import parse from "../../helpers/json-api-parser";
import { getToken } from "../../helpers/utils";
import toRruleString, { fromRruleString } from "../../helpers/rrule";
import { rrulestr } from "rrule";
import { fetchCurrentUserProfile } from "./authSlice";
import { purgeCloudflareCache } from "../../helpers/cloudflareCache";

const daysOfWeek = [
  { value: "MO", label: "Mon" },
  { value: "TU", label: "Tue" },
  { value: "WE", label: "Wed" },
  { value: "TH", label: "Thu" },
  { value: "FR", label: "Fri" },
  { value: "SA", label: "Sat" },
  { value: "SU", label: "Sun" },
];


// Helper functions
export const calcEndTime = (start, duration, tz) => moment(start)
  .utcOffset(tz, true)
  .add(parseInt(duration, 10), 'minutes')
  .toDate();

export const defaultTZ = moment.tz.guess();

const processRecurringEvent = (event) => {
  const duration = event.duration;
  const eventTimeZone = event.tz;

  return event.start.map((start) => {
    // Keep event start/end as absolute UTC instants.
    // UI accessors perform selected-timezone wall-clock rendering.
    const iso = moment.isMoment(start) ? start.toISOString() : start;
    const utcStart = moment.parseZone(iso);
    const utcEnd = utcStart.clone().add(duration, "minutes");

    return {
      ...event,
      start: utcStart.toDate(),
      end: utcEnd.toDate(),
    };
  });
};


const prepareToCall = (event) => {
  const {
    title,
    description,
    tags,
    hosts,
    recurring,
    recurringType,
    days,
    startDate,
    endDate,
    haslongduration,
    startTime,
    duration,
    rrule,
    tz,
    monthlyOption, // option to select when monthlt is selected for recurring events ['','']
    monthlyWeek, // week of the month e.g. 1,2,3,4,5
    monthlyweekDay, // week day of the selected week e.g sunday, monday, tuesday, wednesday
    monthlyDays, // day of the month for repeated events when "DayWise" is selected 
    repeatsEvery, // repeats in X months when selected monthly as option

  } = event;


  const data = {
    title,
    description,
    tags,
    hosts,
    haslongduration,
    tz,
    duration: parseInt(duration, 10),
  };

  if (rrule) {
    data.rrule = rrule;
  } else {
    data.rrule = toRruleString({
      recurring, recurringType, days, startDate, endDate, haslongduration, startTime, duration, tz,
      monthlyOption,
      monthlyWeek,
      monthlyweekDay,
      monthlyDays,
      repeatsEvery
    });
  }

  return data;
};
const shallowEqual = (obj1, obj2) => {
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);
  if (keys1.length !== keys2.length) return false;
  return keys1.every(k => obj1[k] === obj2[k]);
};

const onlyUpdated = (previousData, newData) => {
  const newEvent = prepareToCall({ ...newData });
  const oldEvent = prepareToCall({ ...previousData });

  const keys = Object.keys(newEvent);
  const data = {};

  keys.forEach((key) => {
    const isPlainObject =
      newEvent[key] !== null &&
      !Array.isArray(newEvent[key]) &&
      typeof newEvent[key] === 'object';

    if (!isPlainObject && oldEvent[key] !== newEvent[key]) {
      data[key] = newEvent[key];
    }

    if (isPlainObject && !shallowEqual(oldEvent[key], newEvent[key])) {
      data[key] = newEvent[key];
    }
  });

  const oldRRule = normalizeRRule(oldEvent.rrule);
  const newRRule = normalizeRRule(newEvent.rrule);

  data.duration = newEvent.duration;
  data.tz = newEvent.tz;
  data.rrule = newEvent.rrule;
  return data;
};



const normalizeRRule = (rule) => {
  if (!rule) return null;
  return rrulestr(rule).toString();
};

export const eventsIndex = state => state.schedule.order || [];
export const eventsData = state => state.schedule.data || {};
export const timezoneSelector = state =>
  get(state.auth, 'user.attributes.stationTimezone', defaultTZ);

export const getRecurringEvents = (events, data) => {
  return flatten(events.map(id => processRecurringEvent(data[id])))
};

// Async thunks
export const fetchEvents = createAsyncThunk(
  "schedule/fetchEvents",
  async ({ start, end }, { rejectWithValue, getState, dispatch }) => {

    try {
      await dispatch(fetchCurrentUserProfile(getToken()));
      const { auth } = getState();

      const owner = auth?.user?.attributes?.ownerEmail ?? auth.user?.attributes?.username;
      const timeZone = auth.user?.attributes?.stationTimezone;

      const response = await axiosInstance.post(ENDPOINTS.SCHEDULE.EVENTS, {
        owner,
        startTime: start.toISOString(),
        endTime: end.toISOString(),
      });
      const parsedData = await parse(response.data);

      return { parsedData, timeZone };
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to fetch events");
    }
  }
);

export const fetchExportEvents = createAsyncThunk(
  "schedule/fetchExportEvents",
  async (_, { rejectWithValue, getState }) => {
    try {
      const { auth } = getState();
      const owner = auth?.user?.attributes?.ownerEmail ?? auth.user?.attributes?.username;
      const response = await axiosInstance.post(
        ENDPOINTS.SCHEDULE.EXPORT_EVENTS,
        { owner }
      ).then((resp => {

        let data = resp.data.map((event, index) => {
          const rules = fromRruleString(event.rrule);
          const startTimeDaily = moment(rules.startDate).format('hh:mma');
          const endTime = moment(rules.startDate).add(event.duration, 'minutes').format('hh:mma');

          return {
            startTime: startTimeDaily,
            endingTime: endTime,
            startTime: startTimeDaily,
            ...event
          }

        });
        return data;
      }));
      return response;


    } catch (error) {
      console.log(error.message, 'error')
    }
  }
);

export const fetchTags = createAsyncThunk(
  "schedule/fetchTags",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(ENDPOINTS.SCHEDULE.TAGS, {
        active: false,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to fetch tags");
    }
  }
);

export const createEvent = createAsyncThunk(
  "schedule/createEvent",
  async (values, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const owner = state?.auth?.user?.attributes?.ownerEmail ?? state?.auth.user?.attributes?.username;

      const existingEvents = state.schedule.events;

      let path;
      const { id, recurring, allDay, haslongduration } = values;

      if (!recurring) {
        values.days = [];
        values.recurringType = '';
      }

      if (haslongduration) {
        values.haslongduration = true;
      } else {
        values.haslongduration = false;
      }
      if (allDay) {
        values.startTime = '12:00 am';
        values.duration = 1440;
      }

      // TODO: refresh current view in an easier way?
      // TODO: simply inject the event?
      const data = { token: getToken() };
      if (owner) {
        data.owner = owner;
      }

      let oldEvent = existingEvents.find(e => e.id === id);
      if (oldEvent) {
        path = ENDPOINTS.SCHEDULE.UPDATE_EVENT;
        data.id = id;
        const eventCopy = { ...oldEvent };

        if (eventCopy.days) {
          eventCopy.days = eventCopy.days.map(label => {
            const match = daysOfWeek.find(d => d.label === label);
            return match ? match.value : label; // fallback if no match
          });
        }
        data.event = onlyUpdated(eventCopy, values);
        data.event.haslongduration = values.haslongduration;
      } else {
        path = ENDPOINTS.SCHEDULE.CREATE_EVENT;
        data.event = prepareToCall(values);
      }
      if (Object.keys(data.event).length === 0) {
        return rejectWithValue("no fields have been changed");
      }
      const response = await axiosInstance.post(
        path,
        { ...data }
      );
      const { stationGroup } = getState().auth.user.attributes;
      if (stationGroup) {
        await purgeCloudflareCache(stationGroup);
      }

      return response.data;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to create event");
    }
  }
);

export const updateEvent = createAsyncThunk(
  "schedule/updateEvent",
  async ({ token, id, event }, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const owner = state?.auth?.user?.attributes?.ownerEmail ?? state?.auth.user?.attributes?.username;

      const payload = owner
        ? { token, id, event, owner }
        : { token, id, event };

      const response = await axiosInstance.post(
        ENDPOINTS.SCHEDULE.UPDATE_EVENT,
        payload
      );
      const { stationGroup } = getState().auth.user.attributes;
      if (stationGroup) {
        await purgeCloudflareCache(stationGroup);
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to update event");
    }
  }
);

export const deleteEvent = createAsyncThunk(
  "schedule/deleteEvent",
  async ({ token, id }, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const owner = state?.auth?.user?.attributes?.ownerEmail ?? state?.auth.user?.attributes?.username;

      const payload = owner
        ? { token, id, owner }
        : { token, id };

      await axiosInstance.post(ENDPOINTS.SCHEDULE.REMOVE_EVENT, payload);
      const { stationGroup } = getState().auth.user.attributes;
      if (stationGroup) {
        await purgeCloudflareCache(stationGroup);
      }

      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to delete event");
    }
  }
);

// Initial state
const initialState = {
  events: [],
  eventIndex: [],
  order: [], // Added for recurring events selector
  data: {},  // Added for recurring events selector
  exportEvents: [],
  tags: [],
  tagNames: {},
  loading: false,
  error: null,
  selectedEvent: null,
  isDetailOpen: false,
  currentView: "week",
  currentDate: new Date(),
  timezone: moment.tz.guess(),
};

// Slice
const scheduleSlice = createSlice({
  name: "schedule",
  initialState,
  reducers: {
    setSelectedEvent: (state, action) => {
      state.selectedEvent = action.payload;
    },
    openEventDetail: (state, action) => {

      state.selectedEvent = action.payload;
      state.isDetailOpen = true;
    },
    closeEventDetail: (state) => {
      state.selectedEvent = null;
      state.isDetailOpen = false;
    },
    setCurrentView: (state, action) => {
      state.currentView = action.payload;
    },
    setCurrentDate: (state, action) => {
      state.currentDate = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Events
      .addCase(fetchEvents.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEvents.fulfilled, (state, action) => {
        const { parsedData } = action.payload;
        state.data = parsedData.entities.events;
        state.order = parsedData.result.events;
        state.meta = parsedData.meta;

        state.events = getRecurringEvents(parsedData.result.events, parsedData.entities.events);
        state.loading = false;
      })
      .addCase(fetchEvents.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch Export Events
      .addCase(fetchExportEvents.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchExportEvents.fulfilled, (state, action) => {
        state.loading = false;
        state.exportEvents = action.payload || [];
      })
      .addCase(fetchExportEvents.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch Tags
      .addCase(fetchTags.fulfilled, (state, action) => {
        const tags = action.payload.data || [];
        const tagNames = {};
        const processedTags = tags.map((tag) => {
          const id = tag.id;
          const text = tag.attributes.eng;
          tagNames[id] = text;
          return {
            section: tag.attributes.section,
            id,
            text,
          };
        });

        // Group by section
        const sections = {};
        processedTags.forEach((tag) => {
          if (!sections[tag.section]) {
            sections[tag.section] = [];
          }
          sections[tag.section].push(tag);
        });

        const result = Object.keys(sections).map((title) => ({
          title: title.charAt(0).toUpperCase() + title.slice(1),
          suggestions: sections[title].sort((a, b) =>
            a.text.localeCompare(b.text)
          ),
        }));

        state.tags = result.sort((a, b) => a.title.localeCompare(b.title));
        state.tagNames = tagNames;
      })
      // Create Event
      .addCase(createEvent.fulfilled, (state, action) => {
        // const newEvent = action.payload.data;
        // state.events.push(newEvent);
        // state.order.push(newEvent.id);
        // state.data[newEvent.id] = newEvent;
        state.isDetailOpen = false;
        state.selectedEvent = null;
      })
      .addCase(createEvent.rejected, (state, action) => {
        state.error = action.payload;
      })
      // Update Event
      .addCase(updateEvent.fulfilled, (state, action) => {
        const updatedEvent = action.payload.data;
        const index = state.events.findIndex(
          (event) => event.id === updatedEvent.id
        );
        if (index !== -1) {
          state.events[index] = updatedEvent;
          state.data[updatedEvent.id] = updatedEvent;
        }
        state.isDetailOpen = false;
        state.selectedEvent = null;
      })
      .addCase(updateEvent.rejected, (state, action) => {
        state.error = action.payload;
      })
      // Delete Event
      .addCase(deleteEvent.fulfilled, (state, action) => {
        const deletedId = action.payload;
        state.events = state.events.filter(
          (event) => event.id !== deletedId
        );
        state.order = state.order.filter(id => id !== deletedId);
        delete state.data[deletedId];
        state.isDetailOpen = false;
        state.selectedEvent = null;
      })
      .addCase(deleteEvent.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export const {
  setSelectedEvent,
  openEventDetail,
  closeEventDetail,
  setCurrentView,
  setCurrentDate,
  clearError,
} = scheduleSlice.actions;

export default scheduleSlice.reducer;